/**
 * Helper for the dose input component
 *
 * @see DoseInput.vue
 * @constructor
 */
const DoseInputService = () => {
    return {
        /**
         * Convert a decimal number to a fraction string when value is less than 1.
         * Otherwise, keep the original number as string.
         *
         * E.g. 0.5 -> "1/2", 1.25 -> "1,25", "1,25 -> "1,25"
         */
        convertToFraction(decimal: number): string {
            if (decimal > 1) {
                return decimal.toString().replace('.', ',');
            }

            if (decimal === 0) {
                return '0';
            }

            const fractionMap = {
                0.25: '1/4',
                0.5: '1/2',
                0.75: '3/4',
            };

            const tolerance = 0.001;
            for (const [decimalValue, fraction] of Object.entries(fractionMap)) {
                if (Math.abs(decimal - parseFloat(decimalValue)) < tolerance) {
                    return fraction;
                }
            }

            return decimal.toString().replace('.', ',');
        },

        /**
         * Parse a string input which can be a decimal or a fraction to a decimal number.
         * Returns null if the input is invalid.
         *
         * E.g. "1,5" -> 1.5, "1/2" -> 0.5, "2" -> 2, "abc" -> null
         */
        parseFractionToDecimal(input: string): number | null {
            if (!input || input.trim() === '') {
                return null;
            }

            const trimmed = input.trim().replace(',', '.');

            if (/^\d*\.?\d+$/.test(trimmed)) {
                const decimal = parseFloat(trimmed);

                return isNaN(decimal) ? null : decimal;
            }

            const fractionMatch = trimmed.match(/^(\d+)\/(\d+)$/);
            if (fractionMatch) {
                const numerator = parseInt(fractionMatch[1]);
                const denominator = parseInt(fractionMatch[2]);

                if (denominator === 0) {
                    return null;
                }

                return numerator / denominator;
            }

            const wholeMatch = trimmed.match(/^(\d+)$/);
            if (wholeMatch) {
                return parseInt(wholeMatch[1]);
            }

            return null;
        }
    };
};

export default DoseInputService();
